import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { v4 as uuidv4 } from 'uuid';
import { useMemo } from 'react';
import type { FilterPreset } from '../utils/tql/types';
import { BUILTIN_PRESETS } from '../utils/tql/types';

type FilterPresetsState = {
  presets: FilterPreset[];
  activeQuery: string;
  quickFilterEnabled: boolean;
  showAdvancedFilter: boolean;
  recentQueries: string[];
  setActiveQuery: (query: string) => void;
  setQuickFilterEnabled: (enabled: boolean) => void;
  setShowAdvancedFilter: (show: boolean) => void;
  createPreset: (
    preset: Omit<FilterPreset, 'id' | 'createdAt' | 'updatedAt'>,
  ) => FilterPreset;
  updatePreset: (
    id: string,
    updates: Partial<Omit<FilterPreset, 'id' | 'createdAt'>>,
  ) => void;
  deletePreset: (id: string) => void;
  duplicatePreset: (id: string) => FilterPreset | null;
  getAllPresets: () => FilterPreset[];
  resolvePreset: (nameOrAlias: string) => FilterPreset | undefined;
  addToRecentQueries: (query: string) => void;
  clearRecentQueries: () => void;
  resetToDefaults: () => void;
};

const STORAGE_KEY = 'terra-proxy-filter-presets-store';
const MAX_RECENT_QUERIES = 20;

// Initialize built-in presets with IDs
const initializeBuiltinPresets = (): FilterPreset[] => {
  const now = Date.now();
  return BUILTIN_PRESETS.map((preset) => ({
    ...preset,
    id: `builtin-${preset.alias}`,
    createdAt: now,
    updatedAt: now,
  }));
};

const builtinPresets = initializeBuiltinPresets();

const initialState = {
  presets: [] as FilterPreset[],
  activeQuery: '',
  quickFilterEnabled: false,
  showAdvancedFilter: false,
  recentQueries: [] as string[],
};

export const useFilterPresetsStore = create<FilterPresetsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setActiveQuery: (query: string) => {
        set({ activeQuery: query });
        if (query.trim()) {
          get().addToRecentQueries(query);
        }
      },

      setQuickFilterEnabled: (enabled: boolean) => {
        set({ quickFilterEnabled: enabled });
      },

      setShowAdvancedFilter: (show: boolean) => {
        set({ showAdvancedFilter: show });
      },

      createPreset: (presetData) => {
        const now = Date.now();
        const newPreset: FilterPreset = {
          ...presetData,
          id: uuidv4(),
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          presets: [...state.presets, newPreset],
        }));

        return newPreset;
      },

      updatePreset: (id, updates) => {
        set((state) => ({
          presets: state.presets.map((preset) =>
            preset.id === id
              ? { ...preset, ...updates, updatedAt: Date.now() }
              : preset,
          ),
        }));
      },

      deletePreset: (id) => {
        set((state) => ({
          presets: state.presets.filter((preset) => preset.id !== id),
        }));
      },

      duplicatePreset: (id) => {
        const state = get();
        const allPresets = state.getAllPresets();
        const original = allPresets.find((p) => p.id === id);

        if (!original) return null;

        const now = Date.now();
        const newPreset: FilterPreset = {
          ...original,
          id: uuidv4(),
          name: `${original.name} (Copy)`,
          alias: `${original.alias}-copy`,
          isBuiltIn: false,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          presets: [...state.presets, newPreset],
        }));

        return newPreset;
      },

      getAllPresets: () => {
        const state = get();
        return [...builtinPresets, ...state.presets];
      },

      resolvePreset: (nameOrAlias) => {
        const allPresets = get().getAllPresets();
        const lower = nameOrAlias.toLowerCase();

        let preset = allPresets.find((p) => p.alias.toLowerCase() === lower);

        if (!preset) {
          preset = allPresets.find((p) => p.name.toLowerCase() === lower);
        }

        if (!preset) {
          preset = allPresets.find((p) =>
            p.alias.toLowerCase().startsWith(lower),
          );
        }

        return preset;
      },

      addToRecentQueries: (query) => {
        const trimmed = query.trim();
        if (!trimmed) return;

        set((state) => {
          const filtered = state.recentQueries.filter((q) => q !== trimmed);
          const updated = [trimmed, ...filtered].slice(0, MAX_RECENT_QUERIES);
          return { recentQueries: updated };
        });
      },

      clearRecentQueries: () => {
        set({ recentQueries: [] });
      },

      resetToDefaults: () => {
        set({
          ...initialState,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        presets: state.presets,
        activeQuery: state.activeQuery,
        showAdvancedFilter: state.showAdvancedFilter,
        recentQueries: state.recentQueries,
      }),
    },
  ),
);

export const useActiveQuery = () =>
  useFilterPresetsStore((state) => state.activeQuery);

export const useAllPresets = (): FilterPreset[] => {
  const userPresets = useFilterPresetsStore(
    useShallow((state) => state.presets),
  );

  return useMemo(
    () => [
      ...BUILTIN_PRESETS.map((preset) => ({
        ...preset,
        id: `builtin-${preset.alias}`,
        createdAt: 0,
        updatedAt: 0,
      })),
      ...userPresets,
    ],
    [userPresets],
  );
};

export const usePresetResolver = () =>
  useFilterPresetsStore((state) => state.resolvePreset);

export const useRecentQueries = () =>
  useFilterPresetsStore((state) => state.recentQueries);
